import React, { useState, useMemo } from 'react';
import { 
  LayoutDashboard, Database, Users, Building2, Briefcase, Package, Calendar, 
  Settings, ChevronDown, ChevronRight, Search, Eye, Edit3, Trash2,
  MapPin, Globe, Crown, Tag, ShoppingBag, BookOpen, Layers, FileText,
  BarChart3, GitBranch, Shield, Clock, Truck, Plus, Filter, Key,
  Server, Code, Braces, List, Hash, ArrowLeft, CalendarDays,
  Check, X, Save, RefreshCw, Download, Upload, ExternalLink, Leaf
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { 
  OFFICES, COMPANIES, USERS, CATEGORIES, SERVICES, PRODUCTS, COURSES, RENTALS, EVENTS,
  MODULES, RESOURCES, LOYALTY_PROGRAMS, PLATFORM_CONFIG, BOOKING_STATUSES, ORDER_STATUSES,
  BOOKINGS, ORDERS, COUNTRIES, REGIONS, CITIES, DAILY_PRICING, COURSE_SCHEDULE,
  RESOURCE_TYPES, COMMUNITIES,
  getAllBookingsForOffice, getAllOrdersForOffice,
  getServicesByOfficeId, getProductsByOfficeId, getResourcesByOfficeId,
  getEnabledModulesForOffice, isModuleEnabledForOffice, updateOfficeModules,
  enrichBooking, enrichOrder,
} from '../data/database';

// ============================================================================
// COMPONENTI HELPER
// ============================================================================

// Card per statistiche
const StatCard = ({ label, value, icon: Icon, color = 'emerald', onClick }) => (
  <div 
    className={`bg-white rounded-xl shadow-sm border border-gray-100 p-5 ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
    onClick={onClick}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-500 mb-1">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
      <div className={`w-12 h-12 bg-${color}-50 rounded-xl flex items-center justify-center`}>
        <Icon className={`w-6 h-6 text-${color}-600`} />
      </div>
    </div>
  </div>
);

// Tabella generica
const DataTable = ({ columns, data, onRowClick, emptyMessage = "Nessun dato" }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 border-b border-gray-100">
          <tr>
            {columns.map(col => (
              <th key={col.key} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-8 text-center text-gray-500">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, i) => (
              <tr 
                key={row.id || i} 
                className={onRowClick ? 'hover:bg-gray-50 cursor-pointer' : ''}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {columns.map(col => (
                  <td key={col.key} className="px-4 py-3 text-sm text-gray-700">
                    {col.render ? col.render(row[col.key], row) : row[col.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

// Badge colorato
const Badge = ({ children, color = 'gray' }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-medium bg-${color}-100 text-${color}-700`}>
    {children}
  </span>
);

// Sezione collassabile
const CollapsibleSection = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <h3 className="font-medium text-gray-900">{title}</h3>
        <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && <div className="p-4">{children}</div>}
    </div>
  );
};

// ============================================================================
// SEZIONI ADMIN
// ============================================================================

// Dashboard Overview
const OverviewSection = ({ onNavigate }) => {
  const stats = useMemo(() => ({
    companies: COMPANIES.length,
    offices: OFFICES.length,
    users: USERS.length,
    categories: CATEGORIES.length,
    services: SERVICES.length,
    rentals: RENTALS?.length || 0,
    products: PRODUCTS.length,
    resources: RESOURCES.length,
    courses: COURSES?.length || 0,
    events: EVENTS?.length || 0,
    communities: COMMUNITIES?.length || 0,
    bookings: OFFICES.reduce((sum, o) => sum + getAllBookingsForOffice(o.id).length, 0),
    orders: OFFICES.reduce((sum, o) => sum + getAllOrdersForOffice(o.id).length, 0),
    modules: Object.keys(MODULES).length,
    cities: CITIES?.length || 0,
  }), []);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Dashboard</h2>
        <p className="text-gray-500">Panoramica completa del sistema</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard label="Aziende" value={stats.companies} icon={Building2} color="blue" onClick={() => onNavigate('companies')} />
        <StatCard label="Sedi" value={stats.offices} icon={MapPin} color="emerald" onClick={() => onNavigate('offices')} />
        <StatCard label="Utenti" value={stats.users} icon={Users} color="purple" onClick={() => onNavigate('users')} />
        <StatCard label="Categorie" value={stats.categories} icon={Tag} color="amber" onClick={() => onNavigate('categories')} />
        <StatCard label="Servizi Orari" value={stats.services} icon={Clock} color="cyan" onClick={() => onNavigate('services')} />
        <StatCard label="Noleggi" value={stats.rentals} icon={Key} color="blue" onClick={() => onNavigate('rentals')} />
        <StatCard label="Prodotti" value={stats.products} icon={Package} color="indigo" onClick={() => onNavigate('products')} />
        <StatCard label="Eventi" value={stats.events} icon={CalendarDays} color="rose" onClick={() => onNavigate('events')} />
        <StatCard label="Communities" value={stats.communities} icon={Leaf} color="green" onClick={() => onNavigate('communities')} />
        <StatCard label="Prenotazioni" value={stats.bookings} icon={Calendar} color="slate" onClick={() => onNavigate('bookings')} />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div 
          className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 text-white cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => onNavigate('modules-assign')}
        >
          <Layers className="w-8 h-8 mb-3 opacity-80" />
          <h3 className="font-semibold text-lg mb-1">Gestione Moduli</h3>
          <p className="text-emerald-100 text-sm">Assegna moduli agli office</p>
        </div>
        <div 
          className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => onNavigate('schema')}
        >
          <GitBranch className="w-8 h-8 mb-3 opacity-80" />
          <h3 className="font-semibold text-lg mb-1">Schema Database</h3>
          <p className="text-blue-100 text-sm">Visualizza struttura completa</p>
        </div>
        <div 
          className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => onNavigate('explorer')}
        >
          <Search className="w-8 h-8 mb-3 opacity-80" />
          <h3 className="font-semibold text-lg mb-1">Data Explorer</h3>
          <p className="text-purple-100 text-sm">Esplora tutti i dati</p>
        </div>
      </div>
    </div>
  );
};

// Schema Database Completo
const SchemaSection = () => {
  const [expandedEntities, setExpandedEntities] = useState(['OFFICES', 'MODULES']);
  
  const toggleEntity = (name) => {
    setExpandedEntities(prev => 
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );
  };

  const entities = [
    {
      name: 'COMPANIES',
      count: COMPANIES.length,
      color: 'blue',
      fields: ['id', 'name', 'slug', 'categorySlug', 'isElit'],
      relations: ['OFFICES (1:N)', 'USERS (1:N)'],
    },
    {
      name: 'OFFICES',
      count: OFFICES.length,
      color: 'emerald',
      fields: ['id', 'companyId', 'name', 'slug', 'categorySlug', 'address', 'coordinates{}', 'distance', 'phone', 'email', 'image', 'hours{}', 'rating', 'reviewsCount', 'isElit', 'enabledModules[]', 'enabledSubmodules{}', 'moduleSettings{}'],
      relations: ['COMPANIES (N:1)', 'RESOURCES (1:N)', 'SERVICES (1:N)', 'PRODUCTS (1:N)', 'RENTALS (1:N)', 'EVENTS (1:N)', 'BOOKINGS (1:N)', 'ORDERS (1:N)', 'COMMUNITIES (M:N)'],
    },
    {
      name: 'USERS',
      count: USERS.length,
      color: 'purple',
      fields: ['id', 'email', 'password', 'role', 'firstName', 'lastName', 'companyId', 'starLevel', 'elitLevel'],
      relations: ['COMPANIES (N:1)', 'BOOKINGS (1:N)', 'ORDERS (1:N)'],
    },
    {
      name: 'RESOURCES',
      count: RESOURCES.length,
      color: 'teal',
      fields: ['id', 'officeId', 'type', 'name', 'surname?', 'role?', 'avatar?', 'capacity?', 'basePrice?', 'maxGuests?', 'category?'],
      relations: ['OFFICES (N:1)', 'BOOKINGS (1:N)'],
    },
    {
      name: 'CATEGORIES',
      count: CATEGORIES.length,
      color: 'amber',
      fields: ['id', 'name', 'slug', 'group', 'suggestedModules[]'],
      relations: ['OFFICES (1:N)'],
    },
    {
      name: 'SERVICES',
      count: SERVICES.length,
      color: 'cyan',
      fields: ['id', 'officeId', 'name', 'price', 'duration', 'category'],
      relations: ['OFFICES (N:1)', 'BOOKINGS (1:N)'],
    },
    {
      name: 'PRODUCTS',
      count: PRODUCTS.length,
      color: 'indigo',
      fields: ['id', 'officeId', 'name', 'price', 'stock', 'category', 'image'],
      relations: ['OFFICES (N:1)', 'ORDERS (1:N)'],
    },
    {
      name: 'RENTALS',
      count: RENTALS?.length || 0,
      color: 'blue',
      fields: ['id', 'officeId', 'name', 'resourceId', 'category', 'pricePerDay?', 'pricePerNight?', 'minDays?', 'minStay?', 'maxGuests?', 'maxPassengers?', 'amenities[]', 'images[]'],
      relations: ['OFFICES (N:1)', 'RESOURCES (N:1)', 'BOOKINGS (1:N)'],
    },
    {
      name: 'EVENTS',
      count: EVENTS?.length || 0,
      color: 'rose',
      fields: ['id', 'officeId', 'name', 'description', 'date', 'startTime', 'endTime', 'price', 'maxParticipants', 'currentParticipants', 'category', 'image'],
      relations: ['OFFICES (N:1)'],
    },
    {
      name: 'COMMUNITIES',
      count: COMMUNITIES?.length || 0,
      color: 'green',
      fields: ['id', 'name', 'slug', 'subtitle', 'description', 'logo', 'coverImage', 'phone', 'email', 'website', 'address', 'theme{}', 'officeIds[]', 'categoryIds[]', 'badges[]', 'isActive', 'isFeatured'],
      relations: ['OFFICES (M:N via officeIds)', 'CATEGORIES (M:N via categoryIds)'],
    },
    {
      name: 'MODULES',
      count: Object.keys(MODULES).length,
      color: 'rose',
      fields: ['id', 'name', 'description', 'icon', 'color', 'schema{}', 'partnerSettings{}', 'submodules?{}'],
      relations: ['OFFICES (M:N via enabledModules)'],
    },
    {
      name: 'DAILY_PRICING',
      count: Object.keys(DAILY_PRICING).length,
      color: 'blue',
      fields: ['key: officeId-resourceId', 'basePrice', 'weekendMarkup', 'seasonalPricing[]', 'customDates{}'],
      relations: ['RESOURCES (N:1)'],
    },
    {
      name: 'COURSE_SCHEDULE',
      count: COURSE_SCHEDULE?.length || 0,
      color: 'indigo',
      fields: ['courseId', 'dayOfWeek', 'startTime', 'endTime', 'roomId'],
      relations: ['COURSES (N:1)'],
    },
    {
      name: 'LOYALTY_PROGRAMS',
      count: 3,
      color: 'amber',
      fields: ['star { levels[], referralBonus }', 'elitUser { levels[] }', 'elitPartner { requirements, benefits }'],
      relations: ['USERS (config)'],
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Schema Database</h2>
        <p className="text-gray-500">Struttura completa delle entità e relazioni</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {entities.map(entity => (
          <div key={entity.name} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <button
              onClick={() => toggleEntity(entity.name)}
              className={`w-full px-4 py-3 flex items-center justify-between bg-${entity.color}-50 hover:bg-${entity.color}-100 transition-colors`}
            >
              <div className="flex items-center gap-3">
                <Database className={`w-5 h-5 text-${entity.color}-600`} />
                <span className="font-semibold text-gray-900">{entity.name}</span>
                <Badge color={entity.color}>{entity.count}</Badge>
              </div>
              <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${expandedEntities.includes(entity.name) ? 'rotate-180' : ''}`} />
            </button>
            
            {expandedEntities.includes(entity.name) && (
              <div className="p-4 space-y-3">
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase mb-2">Campi</p>
                  <div className="flex flex-wrap gap-1">
                    {entity.fields.map(field => (
                      <code key={field} className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-700">
                        {field}
                      </code>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase mb-2">Relazioni</p>
                  <div className="flex flex-wrap gap-1">
                    {entity.relations.map(rel => (
                      <span key={rel} className="px-2 py-0.5 bg-blue-50 rounded text-xs text-blue-700">
                        {rel}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Data Explorer
const ExplorerSection = () => {
  const [selectedEntity, setSelectedEntity] = useState('OFFICES');
  const [searchQuery, setSearchQuery] = useState('');
  
  const entityData = {
    OFFICES: { data: OFFICES, label: 'Sedi' },
    COMPANIES: { data: COMPANIES, label: 'Aziende' },
    USERS: { data: USERS, label: 'Utenti' },
    RESOURCES: { data: RESOURCES, label: 'Risorse' },
    SERVICES: { data: SERVICES, label: 'Servizi' },
    PRODUCTS: { data: PRODUCTS, label: 'Prodotti' },
    CATEGORIES: { data: CATEGORIES, label: 'Categorie' },
    MODULES: { data: Object.entries(MODULES).map(([id, v]) => ({ id, ...v })), label: 'Moduli' },
  };

  const currentData = entityData[selectedEntity]?.data || [];
  const filteredData = searchQuery 
    ? currentData.filter(item => JSON.stringify(item).toLowerCase().includes(searchQuery.toLowerCase()))
    : currentData;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Data Explorer</h2>
        <p className="text-gray-500">Esplora e cerca nei dati del database</p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-4">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca nei dati..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>
        <select
          value={selectedEntity}
          onChange={(e) => setSelectedEntity(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          {Object.entries(entityData).map(([key, { label }]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      {/* Results */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
          <span className="font-medium text-gray-900">{entityData[selectedEntity]?.label}</span>
          <span className="text-sm text-gray-500">{filteredData.length} risultati</span>
        </div>
        <div className="max-h-[600px] overflow-auto">
          <pre className="p-4 text-xs text-gray-700 whitespace-pre-wrap">
            {JSON.stringify(filteredData.slice(0, 20), null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );
};

// Entità: Offices
const OfficesSection = () => {
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredOffices = searchQuery
    ? OFFICES.filter(o => 
        o.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        o.category?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : OFFICES;

  const columns = [
    { key: 'id', label: 'ID' },
    { 
      key: 'name', 
      label: 'Nome',
      render: (val, row) => (
        <div className="flex items-center gap-2">
          {row.image && <img src={row.image} alt="" className="w-8 h-8 rounded object-cover" />}
          <span className="font-medium">{val}</span>
        </div>
      )
    },
    { key: 'category', label: 'Categoria' },
    { 
      key: 'enabledModules', 
      label: 'Moduli',
      render: (val) => (
        <div className="flex flex-wrap gap-1">
          {(val || []).map(m => <Badge key={m} color="emerald">{m}</Badge>)}
        </div>
      )
    },
    { 
      key: 'isElit', 
      label: 'Elit',
      render: (val) => val ? <Badge color="amber">ELIT</Badge> : <span className="text-gray-400">-</span>
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Sedi (Office)</h2>
          <p className="text-gray-500">{OFFICES.length} sedi registrate</p>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Cerca sede..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>

      <DataTable 
        columns={columns} 
        data={filteredOffices}
        onRowClick={(row) => setSelectedOffice(row)}
      />

      {/* Detail Modal */}
      {selectedOffice && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedOffice(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">{selectedOffice.name}</h3>
              <button onClick={() => setSelectedOffice(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <pre className="text-xs bg-gray-50 p-4 rounded-lg overflow-auto max-h-96">
                {JSON.stringify(selectedOffice, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Moduli - Assegnazione
const ModulesAssignSection = () => {
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [editingModules, setEditingModules] = useState({ enabledModules: [], moduleConfig: {} });
  const [saveStatus, setSaveStatus] = useState(null);

  const handleSelectOffice = (office) => {
    setSelectedOffice(office);
    setEditingModules({
      enabledModules: office.enabledModules || [],
      moduleConfig: office.moduleConfig || {},
    });
    setSaveStatus(null);
  };

  const handleToggleModule = (moduleId) => {
    setEditingModules(prev => ({
      ...prev,
      enabledModules: prev.enabledModules.includes(moduleId)
        ? prev.enabledModules.filter(id => id !== moduleId)
        : [...prev.enabledModules, moduleId],
    }));
  };

  const handleSave = () => {
    if (!selectedOffice) return;
    updateOfficeModules(selectedOffice.id, editingModules.enabledModules, editingModules.moduleConfig);
    setSaveStatus('saved');
    setTimeout(() => setSaveStatus(null), 2000);
  };

  const moduleColors = {
    services: 'emerald',
    products: 'purple',
    events: 'orange',
    courses: 'indigo',
    orders: 'rose',
    daily_bookings: 'blue',
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Assegna Moduli</h2>
        <p className="text-gray-500">Configura i moduli abilitati per ogni office</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista Office */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 bg-gray-50 border-b border-gray-100">
            <h3 className="font-medium text-gray-900">Seleziona Office</h3>
          </div>
          <div className="max-h-[500px] overflow-y-auto divide-y divide-gray-100">
            {OFFICES.map(office => (
              <div
                key={office.id}
                onClick={() => handleSelectOffice(office)}
                className={`p-3 cursor-pointer transition-colors ${
                  selectedOffice?.id === office.id ? 'bg-emerald-50' : 'hover:bg-gray-50'
                }`}
              >
                <p className="font-medium text-gray-900 text-sm">{office.name}</p>
                <p className="text-xs text-gray-500">{office.category}</p>
                <div className="flex gap-1 mt-1">
                  {(office.enabledModules || []).map(m => (
                    <span key={m} className={`w-2 h-2 rounded-full bg-${moduleColors[m] || 'gray'}-500`}></span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Configurazione Moduli */}
        <div className="lg:col-span-2">
          {selectedOffice ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-4 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-gray-900">{selectedOffice.name}</h3>
                  <p className="text-sm text-gray-500">{selectedOffice.category}</p>
                </div>
                <button
                  onClick={handleSave}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    saveStatus === 'saved' 
                      ? 'bg-emerald-500 text-white' 
                      : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  }`}
                >
                  {saveStatus === 'saved' ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                  {saveStatus === 'saved' ? 'Salvato!' : 'Salva'}
                </button>
              </div>
              <div className="p-4 grid grid-cols-2 gap-3">
                {Object.entries(MODULES).map(([id, module]) => {
                  const isEnabled = editingModules.enabledModules.includes(id);
                  const color = moduleColors[id] || 'gray';
                  return (
                    <div
                      key={id}
                      onClick={() => handleToggleModule(id)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        isEnabled ? `border-${color}-500 bg-${color}-50` : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">{module.name}</span>
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                          isEnabled ? `bg-${color}-500` : 'bg-gray-200'
                        }`}>
                          {isEnabled && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">{module.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
              <Layers className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Seleziona un office per configurare i moduli</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// SEZIONI ENTITÀ
// ============================================================================

// Aziende
const CompaniesSection = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);

  const filteredCompanies = searchQuery
    ? COMPANIES.filter(c => c.name?.toLowerCase().includes(searchQuery.toLowerCase()))
    : COMPANIES;

  const columns = [
    { key: 'id', label: 'ID' },
    { 
      key: 'name', 
      label: 'Nome',
      render: (val, row) => (
        <div className="flex items-center gap-2">
          {row.logo && <img src={row.logo} alt="" className="w-8 h-8 rounded object-cover" />}
          <span className="font-medium">{val}</span>
        </div>
      )
    },
    { key: 'email', label: 'Email' },
    { 
      key: 'id', 
      label: 'Sedi',
      render: (val) => {
        const count = OFFICES.filter(o => o.companyId === val).length;
        return <Badge color="blue">{count} sedi</Badge>;
      }
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Aziende</h2>
        <p className="text-gray-500">{COMPANIES.length} aziende registrate</p>
      </div>
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input type="text" placeholder="Cerca azienda..." value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
      <DataTable columns={columns} data={filteredCompanies} onRowClick={setSelectedCompany} />
      {selectedCompany && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedCompany(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">{selectedCompany.name}</h3>
              <button onClick={() => setSelectedCompany(null)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">
              <pre className="text-xs bg-gray-50 p-4 rounded-lg overflow-auto max-h-96">{JSON.stringify(selectedCompany, null, 2)}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Utenti
const UsersSection = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  const filteredUsers = searchQuery
    ? USERS.filter(u => u.email?.toLowerCase().includes(searchQuery.toLowerCase()) || 
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()))
    : USERS;

  const roleColors = { admin: 'red', partner: 'purple', consumer: 'blue' };

  const columns = [
    { key: 'id', label: 'ID' },
    { 
      key: 'firstName', 
      label: 'Nome',
      render: (val, row) => (
        <div className="flex items-center gap-2">
          {row.avatar && <img src={row.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />}
          <span className="font-medium">{val} {row.lastName}</span>
        </div>
      )
    },
    { key: 'email', label: 'Email' },
    { 
      key: 'role', 
      label: 'Ruolo',
      render: (val) => <Badge color={roleColors[val] || 'gray'}>{val}</Badge>
    },
    { 
      key: 'starLevel', 
      label: 'Star',
      render: (val) => val ? <Badge color="amber">{val}</Badge> : '-'
    },
    { 
      key: 'elitLevel', 
      label: 'Elit',
      render: (val) => val ? <Badge color="purple">{val}</Badge> : '-'
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Utenti</h2>
        <p className="text-gray-500">{USERS.length} utenti registrati</p>
      </div>
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input type="text" placeholder="Cerca utente..." value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
      <DataTable columns={columns} data={filteredUsers} onRowClick={setSelectedUser} />
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedUser(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">{selectedUser.firstName} {selectedUser.lastName}</h3>
              <button onClick={() => setSelectedUser(null)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">
              <pre className="text-xs bg-gray-50 p-4 rounded-lg overflow-auto max-h-96">{JSON.stringify(selectedUser, null, 2)}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Categorie
const CategoriesSection = () => {
  const [selectedGroup, setSelectedGroup] = useState('all');
  const groups = [...new Set(CATEGORIES.map(c => c.group))];
  
  const filteredCategories = selectedGroup === 'all' 
    ? CATEGORIES 
    : CATEGORIES.filter(c => c.group === selectedGroup);

  const groupColors = {
    bellezza: 'pink', salute: 'red', sport: 'orange', food: 'amber',
    travel: 'sky', transport: 'yellow', auto: 'slate', casa: 'teal'
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Categorie</h2>
        <p className="text-gray-500">{CATEGORIES.length} categorie in {groups.length} gruppi</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setSelectedGroup('all')}
          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
            selectedGroup === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}>Tutti ({CATEGORIES.length})</button>
        {groups.map(group => (
          <button key={group} onClick={() => setSelectedGroup(group)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              selectedGroup === group ? `bg-${groupColors[group] || 'gray'}-500 text-white` : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>{group} ({CATEGORIES.filter(c => c.group === group).length})</button>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
        {filteredCategories.map(cat => (
          <div key={cat.id} className={`bg-white rounded-xl p-4 border border-gray-100 hover:shadow-md transition-shadow`}>
            <div className={`w-10 h-10 bg-${groupColors[cat.group] || 'gray'}-100 rounded-lg flex items-center justify-center mb-2`}>
              <Tag className={`w-5 h-5 text-${groupColors[cat.group] || 'gray'}-600`} />
            </div>
            <p className="font-medium text-gray-900 text-sm">{cat.name}</p>
            <p className="text-xs text-gray-500">{cat.slug}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// SEZIONE: RISORSE (Operatori, Camere, Veicoli, Tavoli)
// ============================================================================

const ResourcesSection = () => {
  const [selectedType, setSelectedType] = useState('all');
  const [selectedOffice, setSelectedOffice] = useState('all');
  const [selectedResource, setSelectedResource] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');

  // Tipi di risorsa dal database
  const resourceTypes = [...new Set(RESOURCES.map(r => r.type))];
  
  // Filtra risorse
  let filteredResources = [...RESOURCES];
  if (searchQuery) {
    filteredResources = filteredResources.filter(r => 
      r.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.surname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.role?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }
  if (selectedType !== 'all') {
    filteredResources = filteredResources.filter(r => r.type === selectedType);
  }
  if (selectedOffice !== 'all') {
    filteredResources = filteredResources.filter(r => r.officeId === parseInt(selectedOffice));
  }
  
  // Ordinamento
  filteredResources.sort((a, b) => {
    if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
    if (sortBy === 'type') return (a.type || '').localeCompare(b.type || '');
    if (sortBy === 'office') return (a.officeId || 0) - (b.officeId || 0);
    if (sortBy === 'price') return (a.basePrice || 0) - (b.basePrice || 0);
    return 0;
  });

  const typeLabels = {
    operator: 'Operatori',
    room: 'Sale/Camere',
    table: 'Tavoli',
    vehicle: 'Veicoli',
    instructor: 'Istruttori',
  };

  const typeColors = {
    operator: 'emerald',
    room: 'blue',
    table: 'rose',
    vehicle: 'amber',
    instructor: 'purple',
  };

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'type', label: 'Tipo', render: (val) => <Badge color={typeColors[val] || 'gray'}>{typeLabels[val] || val}</Badge> },
    { key: 'name', label: 'Nome', render: (val, row) => (
      <div className="flex items-center gap-2">
        {row.avatar && <img src={row.avatar} alt="" className="w-8 h-8 rounded-full object-cover" />}
        {row.image && <img src={row.image} alt="" className="w-8 h-8 rounded object-cover" />}
        <span className="font-medium">{val} {row.surname || ''}</span>
      </div>
    )},
    { key: 'officeId', label: 'Office', render: (val) => OFFICES.find(o => o.id === val)?.name || val },
    { key: 'role', label: 'Ruolo/Dettaglio', render: (val, row) => val || row.category || row.area || (row.seats ? `${row.seats} posti` : '') || '-' },
    { key: 'basePrice', label: 'Prezzo Base', render: (val) => val ? `€${val}` : '-' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Risorse</h2>
        <p className="text-gray-500">
          {RESOURCES.length} risorse totali: {resourceTypes.map(t => `${RESOURCES.filter(r => r.type === t).length} ${typeLabels[t] || t}`).join(', ')}
        </p>
      </div>

      {/* Filtri */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Cerca risorse..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <option value="all">Tutti i tipi</option>
          {resourceTypes.map(type => (
            <option key={type} value={type}>{typeLabels[type] || type} ({RESOURCES.filter(r => r.type === type).length})</option>
          ))}
        </select>
        <select
          value={selectedOffice}
          onChange={(e) => setSelectedOffice(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <option value="all">Tutte le sedi</option>
          {OFFICES.map(office => (
            <option key={office.id} value={office.id}>{office.name}</option>
          ))}
        </select>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <option value="name">Ordina per Nome</option>
          <option value="type">Ordina per Tipo</option>
          <option value="office">Ordina per Office</option>
          <option value="price">Ordina per Prezzo</option>
        </select>
      </div>

      {/* Statistiche per tipo */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {resourceTypes.map(type => {
          const count = RESOURCES.filter(r => r.type === type).length;
          const color = typeColors[type] || 'gray';
          return (
            <div 
              key={type}
              onClick={() => setSelectedType(type === selectedType ? 'all' : type)}
              className={`p-4 rounded-xl cursor-pointer transition-all ${
                selectedType === type ? `bg-${color}-100 border-2 border-${color}-300` : 'bg-white border border-gray-100 hover:border-gray-300'
              }`}
            >
              <p className={`text-2xl font-bold text-${color}-600`}>{count}</p>
              <p className="text-sm text-gray-600">{typeLabels[type] || type}</p>
            </div>
          );
        })}
      </div>

      {/* Tabella */}
      <DataTable columns={columns} data={filteredResources} onRowClick={setSelectedResource} />

      {/* Nota esplicativa */}
      <div className="bg-blue-50 rounded-xl p-4">
        <h4 className="font-medium text-blue-900 mb-2">Cosa sono le Risorse?</h4>
        <p className="text-sm text-blue-700">
          Le risorse sono elementi fisici o persone necessarie per erogare servizi. Ogni modulo può richiedere specifici tipi di risorsa:
        </p>
        <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
          <li><strong>Operatori/Istruttori</strong>: Persone che eseguono servizi (parrucchieri, personal trainer)</li>
          <li><strong>Sale/Camere</strong>: Spazi fisici prenotabili (stanze hotel, sale corsi)</li>
          <li><strong>Veicoli</strong>: Mezzi a noleggio (auto, moto, barche)</li>
          <li><strong>Tavoli</strong>: Postazioni ristorante con numero di coperti</li>
        </ul>
      </div>

      {/* Modal Dettaglio */}
      {selectedResource && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedResource(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">{selectedResource.name} {selectedResource.surname || ''}</h3>
              <button onClick={() => setSelectedResource(null)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">
              <pre className="text-xs bg-gray-50 p-4 rounded-lg overflow-auto max-h-80">{JSON.stringify(selectedResource, null, 2)}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Servizi
const ServicesSection = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredServices = searchQuery
    ? SERVICES.filter(s => s.name?.toLowerCase().includes(searchQuery.toLowerCase()))
    : SERVICES;

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Nome', render: (val, row) => (
      <div className="flex items-center gap-2">
        {row.image && <img src={row.image} alt="" className="w-8 h-8 rounded object-cover" />}
        <span className="font-medium">{val}</span>
      </div>
    )},
    { key: 'officeId', label: 'Office', render: (val) => OFFICES.find(o => o.id === val)?.name || val },
    { key: 'price', label: 'Prezzo', render: (val) => `€${val}` },
    { key: 'duration', label: 'Durata', render: (val) => val ? `${val} min` : '-' },
    { key: 'category', label: 'Categoria' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Servizi ad Appuntamento</h2>
        <p className="text-gray-500">{SERVICES.length} servizi registrati (prenotazioni orarie)</p>
      </div>
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input type="text" placeholder="Cerca servizio..." value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
      <DataTable columns={columns} data={filteredServices} />
    </div>
  );
};

// Noleggi e Soggiorni (prenotazioni giornaliere)
const RentalsSection = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredRentals = searchQuery
    ? (RENTALS || []).filter(r => r.name?.toLowerCase().includes(searchQuery.toLowerCase()))
    : (RENTALS || []);

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Nome', render: (val, row) => (
      <div className="flex items-center gap-2">
        {row.images?.[0] && <img src={row.images[0]} alt="" className="w-8 h-8 rounded object-cover" />}
        <span className="font-medium">{val}</span>
      </div>
    )},
    { key: 'officeId', label: 'Office', render: (val) => OFFICES.find(o => o.id === val)?.name || val },
    { key: 'pricePerNight', label: 'Prezzo', render: (val, row) => val ? `€${val}/notte` : `€${row.pricePerDay}/giorno` },
    { key: 'category', label: 'Categoria', render: (val) => (
      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">{val}</span>
    )},
    { key: 'maxGuests', label: 'Capacità', render: (val, row) => val ? `${val} ospiti` : row.maxPassengers ? `${row.maxPassengers} passeggeri` : '-' },
    { key: 'minStay', label: 'Soggiorno Min', render: (val, row) => val ? `${val} notti` : row.minDays ? `${row.minDays} giorni` : '-' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Noleggi e Soggiorni</h2>
        <p className="text-gray-500">{(RENTALS || []).length} offerte registrate (prenotazioni giornaliere)</p>
      </div>
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input type="text" placeholder="Cerca noleggio o soggiorno..." value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <DataTable columns={columns} data={filteredRentals} />
    </div>
  );
};

// Prodotti
const ProductsSection = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredProducts = searchQuery
    ? PRODUCTS.filter(p => p.name?.toLowerCase().includes(searchQuery.toLowerCase()))
    : PRODUCTS;

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Nome', render: (val, row) => (
      <div className="flex items-center gap-2">
        {row.image && <img src={row.image} alt="" className="w-8 h-8 rounded object-cover" />}
        <span className="font-medium">{val}</span>
      </div>
    )},
    { key: 'officeId', label: 'Office', render: (val) => OFFICES.find(o => o.id === val)?.name || val },
    { key: 'price', label: 'Prezzo', render: (val) => `€${val}` },
    { key: 'stock', label: 'Stock', render: (val) => val !== undefined ? val : '-' },
    { key: 'category', label: 'Categoria' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Prodotti</h2>
        <p className="text-gray-500">{PRODUCTS.length} prodotti registrati</p>
      </div>
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input type="text" placeholder="Cerca prodotto..." value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
        />
      </div>
      <DataTable columns={columns} data={filteredProducts} />
    </div>
  );
};

// Eventi (placeholder - il modulo events non ha ancora dati demo)
const EventsSection = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedOffice, setSelectedOffice] = useState('all');
  const [selectedEvent, setSelectedEvent] = useState(null);
  
  const events = EVENTS || [];
  const categories = [...new Set(events.map(e => e.category))];
  const officesWithEvents = [...new Set(events.map(e => e.officeId))].map(id => OFFICES.find(o => o.id === id)).filter(Boolean);
  
  // Filtra eventi
  let filteredEvents = [...events];
  if (searchQuery) {
    filteredEvents = filteredEvents.filter(e => 
      e.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      e.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }
  if (selectedCategory !== 'all') {
    filteredEvents = filteredEvents.filter(e => e.category === selectedCategory);
  }
  if (selectedOffice !== 'all') {
    filteredEvents = filteredEvents.filter(e => e.officeId === parseInt(selectedOffice));
  }
  
  const categoryColors = {
    wellness: 'purple',
    fitness: 'orange',
    workshop: 'blue',
    community: 'rose',
  };
  
  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Nome', render: (val, row) => (
      <div className="flex items-center gap-2">
        {row.image && <img src={row.image} alt="" className="w-10 h-10 rounded object-cover" />}
        <div>
          <span className="font-medium">{val}</span>
          <p className="text-xs text-gray-500 truncate max-w-[200px]">{row.description}</p>
        </div>
      </div>
    )},
    { key: 'category', label: 'Categoria', render: (val) => <Badge color={categoryColors[val] || 'gray'}>{val}</Badge> },
    { key: 'officeId', label: 'Office', render: (val) => OFFICES.find(o => o.id === val)?.name || val },
    { key: 'date', label: 'Data', render: (val, row) => (
      <div>
        <p className="font-medium">{new Date(val).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}</p>
        <p className="text-xs text-gray-500">{row.startTime} - {row.endTime}</p>
      </div>
    )},
    { key: 'price', label: 'Prezzo', render: (val) => val === 0 ? <span className="text-emerald-600 font-medium">Gratis</span> : `€${val}` },
    { key: 'currentParticipants', label: 'Partecipanti', render: (val, row) => {
      const percentage = (val / row.maxParticipants) * 100;
      return (
        <div className="flex items-center gap-2">
          <div className="w-16 h-2 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full ${percentage > 80 ? 'bg-red-500' : 'bg-emerald-500'}`}
              style={{ width: `${percentage}%` }}
            />
          </div>
          <span className="text-sm">{val}/{row.maxParticipants}</span>
        </div>
      );
    }},
    { key: 'status', label: 'Stato', render: (val) => (
      <Badge color={val === 'active' ? 'emerald' : 'gray'}>{val === 'active' ? 'Attivo' : 'Inattivo'}</Badge>
    )},
  ];

  // Statistiche
  const stats = {
    total: events.length,
    active: events.filter(e => e.status === 'active').length,
    free: events.filter(e => e.price === 0).length,
    totalParticipants: events.reduce((sum, e) => sum + e.currentParticipants, 0),
    totalRevenue: events.reduce((sum, e) => sum + (e.price * e.currentParticipants), 0),
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Eventi</h2>
        <p className="text-gray-500">{events.length} eventi registrati • {stats.totalParticipants} partecipanti totali</p>
      </div>

      {/* Statistiche */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-white border border-gray-100 rounded-xl p-4">
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          <p className="text-sm text-gray-500">Eventi Totali</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4">
          <p className="text-2xl font-bold text-emerald-600">{stats.active}</p>
          <p className="text-sm text-gray-500">Eventi Attivi</p>
        </div>
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
          <p className="text-2xl font-bold text-blue-600">{stats.free}</p>
          <p className="text-sm text-gray-500">Eventi Gratuiti</p>
        </div>
        <div className="bg-purple-50 border border-purple-100 rounded-xl p-4">
          <p className="text-2xl font-bold text-purple-600">{stats.totalParticipants}</p>
          <p className="text-sm text-gray-500">Partecipanti</p>
        </div>
        <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
          <p className="text-2xl font-bold text-amber-600">€{stats.totalRevenue}</p>
          <p className="text-sm text-gray-500">Ricavi Eventi</p>
        </div>
      </div>

      {/* Filtri */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Cerca eventi..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <option value="all">Tutte le categorie</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat} ({events.filter(e => e.category === cat).length})</option>
          ))}
        </select>
        <select
          value={selectedOffice}
          onChange={(e) => setSelectedOffice(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          <option value="all">Tutti gli office</option>
          {officesWithEvents.map(office => (
            <option key={office.id} value={office.id}>{office.name}</option>
          ))}
        </select>
      </div>

      {/* Tabella */}
      <DataTable columns={columns} data={filteredEvents} onRowClick={setSelectedEvent} emptyMessage="Nessun evento trovato" />

      {/* Modal Dettaglio */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedEvent(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="relative h-48">
              <img src={selectedEvent.image} alt={selectedEvent.name} className="w-full h-full object-cover" />
              <button 
                onClick={() => setSelectedEvent(null)} 
                className="absolute top-4 right-4 p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white"
              >
                <X className="w-5 h-5" />
              </button>
              <div className="absolute bottom-4 left-4 flex gap-2">
                <Badge color={categoryColors[selectedEvent.category] || 'gray'}>{selectedEvent.category}</Badge>
                {selectedEvent.price === 0 && <Badge color="emerald">Gratuito</Badge>}
              </div>
            </div>
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">{selectedEvent.name}</h3>
              <p className="text-gray-600 mb-4">{selectedEvent.description}</p>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-500">Data e Ora</p>
                  <p className="font-medium">{new Date(selectedEvent.date).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                  <p className="text-sm">{selectedEvent.startTime} - {selectedEvent.endTime}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Prezzo</p>
                  <p className="font-medium text-lg">{selectedEvent.price === 0 ? 'Gratuito' : `€${selectedEvent.price}`}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Partecipanti</p>
                  <p className="font-medium">{selectedEvent.currentParticipants} / {selectedEvent.maxParticipants}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Office</p>
                  <p className="font-medium">{OFFICES.find(o => o.id === selectedEvent.officeId)?.name}</p>
                </div>
              </div>
              {selectedEvent.tags && (
                <div className="flex flex-wrap gap-2">
                  {selectedEvent.tags.map((tag, idx) => (
                    <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">#{tag}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Corsi
const CoursesSection = () => {
  const courses = COURSES || [];
  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Nome', render: (val, row) => (
      <div className="flex items-center gap-2">
        {row.image && <img src={row.image} alt="" className="w-8 h-8 rounded object-cover" />}
        <span className="font-medium">{val}</span>
      </div>
    )},
    { key: 'officeId', label: 'Office', render: (val) => OFFICES.find(o => o.id === val)?.name || val },
    { key: 'price', label: 'Prezzo', render: (val) => `€${val}` },
    { key: 'duration', label: 'Durata', render: (val) => val || '-' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Corsi</h2>
        <p className="text-gray-500">{courses.length} corsi registrati</p>
      </div>
      <DataTable columns={columns} data={courses} emptyMessage="Nessun corso registrato" />
    </div>
  );
};

// ============================================================================
// SEZIONI TRANSAZIONI
// ============================================================================

// Prenotazioni
const BookingsSection = () => {
  const allBookings = useMemo(() => {
    return OFFICES.flatMap(office => 
      getAllBookingsForOffice(office.id).map(b => ({ ...b, officeName: office.name }))
    );
  }, []);

  const statusColors = { pending: 'amber', confirmed: 'emerald', completed: 'blue', cancelled: 'red' };

  // Badge colors for module types
  const moduleColors = { appointments: 'emerald', tables: 'rose', rentals: 'blue', courses: 'purple' };

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'moduleType', label: 'Tipo', render: (val) => (
      <Badge color={moduleColors[val] || 'gray'}>{val || 'service'}</Badge>
    )},
    { key: 'officeName', label: 'Office' },
    { key: 'serviceName', label: 'Servizio/Risorsa', render: (val, row) => {
      if (row.moduleType === 'tables') return `Tavolo - ${row.guests || 0} ospiti`;
      if (row.moduleType === 'rentals') return row.checkIn ? `${row.checkIn} → ${row.checkOut}` : val;
      return val || '-';
    }},
    { key: 'date', label: 'Data', render: (val, row) => row.checkIn || val },
    { key: 'time', label: 'Ora', render: (val, row) => row.checkIn ? '-' : val },
    { key: 'status', label: 'Stato', render: (val) => <Badge color={statusColors[val] || 'gray'}>{val}</Badge> },
    { key: 'total', label: 'Totale', render: (val, row) => val ? `€${val}` : (row.moduleType === 'tables' ? 'Tavolo' : '-') },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Prenotazioni</h2>
        <p className="text-gray-500">{allBookings.length} prenotazioni totali</p>
      </div>
      {/* Stats per modulo */}
      <div className="grid grid-cols-4 gap-3">
        {['appointments', 'tables', 'rentals', 'courses'].map(mod => {
          const count = allBookings.filter(b => b.moduleType === mod).length;
          return (
            <div key={mod} className={`bg-${moduleColors[mod]}-50 rounded-xl p-4 border border-${moduleColors[mod]}-100`}>
              <p className="text-sm text-gray-600">{mod}</p>
              <p className={`text-2xl font-bold text-${moduleColors[mod]}-600`}>{count}</p>
            </div>
          );
        })}
      </div>
      <DataTable columns={columns} data={allBookings} emptyMessage="Nessuna prenotazione" />
    </div>
  );
};

// Ordini
const OrdersSection = () => {
  const allOrders = useMemo(() => {
    return OFFICES.flatMap(office => 
      getAllOrdersForOffice(office.id).map(o => ({ ...o, officeName: office.name }))
    );
  }, []);

  const statusColors = { pending: 'amber', processing: 'blue', shipped: 'purple', delivered: 'emerald', ready: 'cyan', cancelled: 'red' };
  const fulfillmentColors = { shipping: 'blue', delivery: 'amber', takeaway: 'emerald' };
  const fulfillmentLabels = { shipping: 'Spedizione', delivery: 'Consegna', takeaway: 'Ritiro' };

  const columns = [
    { key: 'id', label: 'ID' },
    { key: 'officeName', label: 'Office' },
    { key: 'fulfillmentType', label: 'Tipo', render: (val) => (
      <Badge color={fulfillmentColors[val] || 'gray'}>{fulfillmentLabels[val] || val}</Badge>
    )},
    { key: 'date', label: 'Data' },
    { key: 'itemsCount', label: 'Prodotti', render: (_, row) => `${row.items?.length || 0} articoli` },
    { key: 'status', label: 'Stato', render: (val) => <Badge color={statusColors[val] || 'gray'}>{val}</Badge> },
    { key: 'total', label: 'Totale', render: (val) => `€${val?.toFixed(2) || 0}` },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Ordini</h2>
        <p className="text-gray-500">{allOrders.length} ordini totali</p>
      </div>
      {/* Stats per fulfillment type */}
      <div className="grid grid-cols-3 gap-3">
        {['shipping', 'delivery', 'takeaway'].map(type => {
          const orders = allOrders.filter(o => o.fulfillmentType === type);
          const total = orders.reduce((sum, o) => sum + (o.total || 0), 0);
          return (
            <div key={type} className={`bg-${fulfillmentColors[type]}-50 rounded-xl p-4 border border-${fulfillmentColors[type]}-100`}>
              <p className="text-sm text-gray-600">{fulfillmentLabels[type]}</p>
              <p className={`text-2xl font-bold text-${fulfillmentColors[type]}-600`}>{orders.length}</p>
              <p className="text-xs text-gray-500">€{total.toFixed(2)}</p>
            </div>
          );
        })}
      </div>
      <DataTable columns={columns} data={allOrders} emptyMessage="Nessun ordine" />
    </div>
  );
};

// ============================================================================
// SEZIONI CONFIGURAZIONE MODULI
// ============================================================================

// Configurazione Schema Moduli
// ============================================================================
// SEZIONE: CONFIGURAZIONE MODULI (Nuova Architettura)
// ============================================================================

const ModulesConfigSection = () => {
  const modules = Object.entries(MODULES).map(([id, data]) => ({ id, ...data }));
  const [selectedModule, setSelectedModule] = useState(null);

  const moduleColors = {
    appointments: 'emerald', rentals: 'blue', courses: 'indigo',
    events: 'orange', ecommerce: 'purple', tables: 'rose'
  };

  const moduleIcons = {
    appointments: Calendar, rentals: CalendarDays, courses: BookOpen,
    events: Calendar, ecommerce: ShoppingBag, tables: MapPin
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Configurazione Moduli</h2>
        <p className="text-gray-500">{modules.length} moduli disponibili nel sistema</p>
      </div>

      {/* Card Moduli */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {modules.map(module => {
          const Icon = moduleIcons[module.id] || Layers;
          const color = moduleColors[module.id] || 'gray';
          const hasSubmodules = module.submodules && Object.keys(module.submodules).length > 0;
          
          return (
            <div 
              key={module.id} 
              onClick={() => setSelectedModule(module)}
              className={`bg-white rounded-xl p-5 border-2 border-gray-100 hover:border-${color}-300 cursor-pointer transition-all hover:shadow-md`}
            >
              <div className={`w-12 h-12 bg-${color}-100 rounded-xl flex items-center justify-center mb-3`}>
                <Icon className={`w-6 h-6 text-${color}-600`} />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">{module.name}</h3>
              <p className="text-sm text-gray-500 mb-3 line-clamp-2">{module.description}</p>
              <div className="flex flex-wrap gap-1">
                <Badge color={color}>{module.schema?.pricingType || 'fixed'}</Badge>
                <Badge color={module.schema?.bookingUnit === 'day' ? 'blue' : 'emerald'}>
                  {module.schema?.bookingUnit || 'item'}
                </Badge>
                {module.schema?.requiresResource && <Badge color="amber">risorsa</Badge>}
                {hasSubmodules && <Badge color="purple">{Object.keys(module.submodules).length} sub-moduli</Badge>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal Dettaglio Modulo */}
      {selectedModule && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedModule(null)}>
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <div className="flex items-center gap-3">
                {(() => {
                  const Icon = moduleIcons[selectedModule.id] || Layers;
                  const color = moduleColors[selectedModule.id] || 'gray';
                  return (
                    <div className={`w-10 h-10 bg-${color}-100 rounded-lg flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 text-${color}-600`} />
                    </div>
                  );
                })()}
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{selectedModule.name}</h3>
                  <p className="text-sm text-gray-500">{selectedModule.description}</p>
                </div>
              </div>
              <button onClick={() => setSelectedModule(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Schema Strutturale */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Database className="w-4 h-4" />
                  Schema Strutturale
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Richiede Risorsa</p>
                    <p className="font-medium text-gray-900">{selectedModule.schema?.requiresResource ? 'Sì' : 'No'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Tipo Prezzo</p>
                    <p className="font-medium text-gray-900">{selectedModule.schema?.pricingType || 'fixed'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">Unità Prenotazione</p>
                    <p className="font-medium text-gray-900">{selectedModule.schema?.bookingUnit || 'item'}</p>
                  </div>
                  {selectedModule.schema?.resourceTypes && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-1">Tipi Risorsa</p>
                      <p className="font-medium text-gray-900 text-sm">{selectedModule.schema.resourceTypes.join(', ')}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Sub-Moduli (solo per ecommerce) */}
              {selectedModule.submodules && Object.keys(selectedModule.submodules).length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Layers className="w-4 h-4" />
                    Sub-Moduli
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {Object.entries(selectedModule.submodules).map(([subId, subModule]) => (
                      <div key={subId} className={`border rounded-xl p-4 ${subModule.required ? 'border-emerald-200 bg-emerald-50/50' : 'border-gray-200'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <h5 className="font-medium text-gray-900">{subModule.name}</h5>
                          {subModule.required && <Badge color="emerald">Sempre attivo</Badge>}
                        </div>
                        <p className="text-sm text-gray-500 mb-3">{subModule.description}</p>
                        
                        {/* Impostazioni Partner del sub-modulo */}
                        {subModule.partnerSettings && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <p className="text-xs font-medium text-gray-500 mb-2">Configurazione Partner:</p>
                            <div className="space-y-1">
                              {Object.entries(subModule.partnerSettings).map(([key, setting]) => (
                                <div key={key} className="flex items-center justify-between text-xs">
                                  <span className="text-gray-600">{setting.label}</span>
                                  <span className="text-gray-400">{setting.type} = {String(setting.default)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Impostazioni Partner */}
              {selectedModule.partnerSettings && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Impostazioni Configurabili dal Partner
                  </h4>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 text-gray-500 font-medium">Campo</th>
                          <th className="text-left py-2 text-gray-500 font-medium">Tipo</th>
                          <th className="text-left py-2 text-gray-500 font-medium">Default</th>
                          <th className="text-left py-2 text-gray-500 font-medium">Descrizione</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(selectedModule.partnerSettings).map(([key, setting]) => (
                          <tr key={key} className="border-b border-gray-100 last:border-0">
                            <td className="py-2 font-mono text-gray-900">{key}</td>
                            <td className="py-2"><Badge color="blue">{setting.type}</Badge></td>
                            <td className="py-2 text-gray-600">{String(setting.default)}</td>
                            <td className="py-2 text-gray-500">{setting.label}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// SEZIONI GEOGRAFIA
// ============================================================================

// Locations (Paesi, Regioni, Città)
const LocationsSection = () => {
  const [activeTab, setActiveTab] = useState('countries');
  const countries = COUNTRIES || [];
  const regions = REGIONS || [];
  const cities = CITIES || [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Locations</h2>
        <p className="text-gray-500">Gestione geografica: {countries.length} paesi, {regions.length} regioni, {cities.length} città</p>
      </div>
      <div className="flex gap-2">
        {[
          { id: 'countries', label: `Paesi (${countries.length})` },
          { id: 'regions', label: `Regioni (${regions.length})` },
          { id: 'cities', label: `Città (${cities.length})` },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === tab.id ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>{tab.label}</button>
        ))}
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 max-h-[500px] overflow-auto">
        <pre className="text-xs text-gray-700">
          {activeTab === 'countries' && JSON.stringify(countries, null, 2)}
          {activeTab === 'regions' && JSON.stringify(regions, null, 2)}
          {activeTab === 'cities' && JSON.stringify(cities, null, 2)}
        </pre>
      </div>
    </div>
  );
};

// Community (placeholder - feature futura)
const CommunitySection = () => {
  const communities = COMMUNITIES || [];
  const [searchQuery, setSearchQuery] = useState('');
  
  const filteredCommunities = communities.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Community</h2>
          <p className="text-gray-500">Gestione raggruppamenti tematici di attività</p>
        </div>
        <button className="flex items-center gap-2 bg-emerald-500 text-white px-4 py-2 rounded-lg hover:bg-emerald-600 transition-colors">
          <Plus className="w-4 h-4" />
          Nuova Community
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <p className="text-sm text-gray-500">Community Totali</p>
          <p className="text-2xl font-bold text-gray-900">{communities.length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <p className="text-sm text-gray-500">Community Attive</p>
          <p className="text-2xl font-bold text-emerald-600">{communities.filter(c => c.isActive !== false).length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <p className="text-sm text-gray-500">Community in Evidenza</p>
          <p className="text-2xl font-bold text-amber-600">{communities.filter(c => c.isFeatured).length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-100">
          <p className="text-sm text-gray-500">Attività Collegate</p>
          <p className="text-2xl font-bold text-blue-600">
            {communities.reduce((acc, c) => acc + (c.officeIds?.length || 0), 0)}
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="text"
          placeholder="Cerca community..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
        />
      </div>

      {/* Communities List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredCommunities.map((community) => (
          <div 
            key={community.id}
            className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow"
          >
            <div className={`h-32 bg-gradient-to-r ${community.theme?.gradient || 'from-emerald-500 to-green-600'} relative`}>
              {community.coverImage && (
                <img src={community.coverImage} alt={community.name} className="absolute inset-0 w-full h-full object-cover opacity-30" />
              )}
              <div className="absolute top-3 right-3 flex gap-2">
                {community.isActive !== false && (
                  <span className="bg-white/90 text-emerald-600 text-xs font-medium px-2 py-1 rounded-full">
                    Attiva
                  </span>
                )}
                {community.isFeatured && (
                  <span className="bg-amber-500 text-white text-xs font-medium px-2 py-1 rounded-full">
                    In Evidenza
                  </span>
                )}
              </div>
              <div className="absolute bottom-3 left-3">
                <div className="w-14 h-14 bg-white rounded-xl overflow-hidden shadow-lg">
                  {community.logo ? (
                    <img src={community.logo} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-emerald-100">
                      <Leaf className="w-6 h-6 text-emerald-600" />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="p-4">
              <h3 className="font-bold text-lg text-gray-900">{community.name}</h3>
              <p className="text-sm text-gray-500 mt-0.5">{community.subtitle || community.tagline}</p>
              <p className="text-xs text-gray-400 mt-2 line-clamp-2">{community.description}</p>
              
              <div className="flex items-center gap-4 mt-4 text-sm">
                <div className="flex items-center gap-1 text-gray-500">
                  <Building2 className="w-4 h-4" />
                  <span>{community.officeIds?.length || 0} attività</span>
                </div>
                <div className="flex items-center gap-1 text-gray-500">
                  <Tag className="w-4 h-4" />
                  <span>{community.categoryIds?.length || 0} categorie</span>
                </div>
              </div>

              {community.badges && community.badges.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {community.badges.slice(0, 3).map((badge, i) => (
                    <span key={i} className={`text-xs px-2 py-1 rounded-full bg-${badge.color || 'gray'}-100 text-${badge.color || 'gray'}-700`}>
                      {badge.label}
                    </span>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                <button className="flex-1 flex items-center justify-center gap-1 text-gray-600 hover:text-emerald-600 py-2 rounded-lg hover:bg-emerald-50 transition-colors">
                  <Eye className="w-4 h-4" />
                  Visualizza
                </button>
                <button className="flex-1 flex items-center justify-center gap-1 text-gray-600 hover:text-blue-600 py-2 rounded-lg hover:bg-blue-50 transition-colors">
                  <Edit3 className="w-4 h-4" />
                  Modifica
                </button>
                <button className="flex items-center justify-center text-gray-400 hover:text-red-500 p-2 rounded-lg hover:bg-red-50 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredCommunities.length === 0 && (
        <div className="bg-white rounded-xl p-8 text-center border border-gray-100">
          <Leaf className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-2">Nessuna community trovata</p>
          <p className="text-xs text-gray-400">Prova a modificare la ricerca o crea una nuova community</p>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// SEZIONI PROGRAMMI FEDELTÀ
// ============================================================================

// Programma Star
const StarProgramSection = () => {
  const starConfig = LOYALTY_PROGRAMS?.star || {};
  const levels = starConfig.levels || [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Programma Star</h2>
        <p className="text-gray-500">Configurazione sistema referral</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {levels.map((level, i) => (
          <div key={i} className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-5 border border-amber-200">
            <Crown className="w-8 h-8 text-amber-500 mb-3" />
            <h3 className="font-bold text-gray-900 text-lg mb-1">{level.name}</h3>
            <p className="text-sm text-gray-600 mb-3">Requisito: {level.minReferrals} referrals</p>
            <div className="space-y-1">
              <p className="text-xs text-gray-500">Sconto: {level.discount}%</p>
              <p className="text-xs text-gray-500">Commissione: {level.commission}%</p>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-xl p-5 border border-gray-100">
        <h4 className="font-medium text-gray-700 mb-3">Configurazione Completa</h4>
        <pre className="text-xs bg-gray-50 p-4 rounded-lg overflow-auto max-h-60">{JSON.stringify(starConfig, null, 2)}</pre>
      </div>
    </div>
  );
};

// Programma Elit
const ElitProgramSection = () => {
  const elitUserConfig = LOYALTY_PROGRAMS?.elitUser || {};
  const elitPartnerConfig = LOYALTY_PROGRAMS?.elitPartner || {};
  const userLevels = elitUserConfig.levels || [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Programma Elit</h2>
        <p className="text-gray-500">Configurazione programmi fedeltà</p>
      </div>
      
      <div>
        <h3 className="font-semibold text-gray-900 mb-3">Elit User - Livelli</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {userLevels.map((level, i) => (
            <div key={i} className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-5 border border-purple-200">
              <Shield className="w-8 h-8 text-purple-500 mb-3" />
              <h3 className="font-bold text-gray-900 text-lg mb-1">{level.name}</h3>
              <p className="text-sm text-gray-600 mb-3">Requisito: {level.minPurchases} acquisti</p>
              <div className="space-y-1">
                <p className="text-xs text-gray-500">Sconto: {level.discount}%</p>
                <p className="text-xs text-gray-500">Cashback: {level.cashback}%</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl p-5 border border-gray-100">
          <h4 className="font-medium text-gray-700 mb-3">Elit User Config</h4>
          <pre className="text-xs bg-gray-50 p-4 rounded-lg overflow-auto max-h-40">{JSON.stringify(elitUserConfig, null, 2)}</pre>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-100">
          <h4 className="font-medium text-gray-700 mb-3">Elit Partner Config</h4>
          <pre className="text-xs bg-gray-50 p-4 rounded-lg overflow-auto max-h-40">{JSON.stringify(elitPartnerConfig, null, 2)}</pre>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// SEZIONI CONFIGURAZIONE PIATTAFORMA
// ============================================================================

// Stati
const StatusesSection = () => {
  const bookingStatuses = BOOKING_STATUSES || [];
  const orderStatuses = ORDER_STATUSES || [];

  const statusColors = { 
    pending: 'amber', confirmed: 'emerald', completed: 'blue', cancelled: 'red',
    processing: 'blue', shipped: 'purple', delivered: 'emerald'
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Stati</h2>
        <p className="text-gray-500">Configurazione stati prenotazioni e ordini</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-5 border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4">Stati Prenotazione</h3>
          <div className="flex flex-wrap gap-2">
            {bookingStatuses.map((status, i) => (
              <Badge key={i} color={statusColors[status] || 'gray'}>{status}</Badge>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4">Stati Ordine</h3>
          <div className="flex flex-wrap gap-2">
            {orderStatuses.map((status, i) => (
              <Badge key={i} color={statusColors[status] || 'gray'}>{status}</Badge>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Piattaforma
const PlatformSection = () => {
  const platformConfig = PLATFORM_CONFIG || {};

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Configurazione Piattaforma</h2>
        <p className="text-gray-500">Impostazioni generali del sistema</p>
      </div>
      <div className="bg-white rounded-xl p-5 border border-gray-100">
        <pre className="text-xs bg-gray-50 p-4 rounded-lg overflow-auto">{JSON.stringify(platformConfig, null, 2)}</pre>
      </div>
    </div>
  );
};

// Relazioni Entità (Diagramma)
const RelationsSection = () => {
  const relations = [
    { from: 'COMPANIES', to: 'OFFICES', type: '1:N', description: 'Una company ha molte sedi' },
    { from: 'COMPANIES', to: 'USERS', type: '1:N', description: 'Una company ha molti utenti (partner)' },
    { from: 'OFFICES', to: 'RESOURCES', type: '1:N', description: 'Un office ha molte risorse' },
    { from: 'OFFICES', to: 'SERVICES', type: '1:N', description: 'Un office ha molti servizi' },
    { from: 'OFFICES', to: 'PRODUCTS', type: '1:N', description: 'Un office ha molti prodotti' },
    { from: 'OFFICES', to: 'COURSES', type: '1:N', description: 'Un office ha molti corsi' },
    { from: 'OFFICES', to: 'MODULES', type: 'M:N', description: 'Office-Moduli via enabledModules[]' },
    { from: 'USERS', to: 'BOOKINGS', type: '1:N', description: 'Un utente ha molte prenotazioni' },
    { from: 'USERS', to: 'ORDERS', type: '1:N', description: 'Un utente ha molti ordini' },
    { from: 'CATEGORIES', to: 'OFFICES', type: '1:N', description: 'Una categoria ha molte sedi' },
    { from: 'RESOURCES', to: 'BOOKINGS', type: '1:N', description: 'Una risorsa ha molte prenotazioni' },
    { from: 'RESOURCES', to: 'DAILY_PRICING', type: '1:1', description: 'Una risorsa ha prezzi giornalieri' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Relazioni Entità</h2>
        <p className="text-gray-500">Diagramma delle relazioni tra entità del database</p>
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Da</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">A</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrizione</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {relations.map((rel, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-3"><Badge color="blue">{rel.from}</Badge></td>
                  <td className="px-4 py-3"><span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">{rel.type}</span></td>
                  <td className="px-4 py-3"><Badge color="emerald">{rel.to}</Badge></td>
                  <td className="px-4 py-3 text-sm text-gray-600">{rel.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// SITE MAP - Struttura completa dell'applicazione
// ============================================================================
const SiteMapSection = () => {
  const [expandedSections, setExpandedSections] = useState(['consumer', 'partner', 'admin']);
  
  const toggleSection = (section) => {
    setExpandedSections(prev => 
      prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section]
    );
  };

  const fileStructure = {
    core: {
      title: 'Core Files',
      icon: 'Server',
      color: 'purple',
      files: [
        { name: 'SkipresApp.jsx', desc: 'Router principale, gestione navigazione' },
        { name: 'AppContext.jsx', desc: 'Stato globale, autenticazione, preferiti, carrello' },
        { name: 'database.js', desc: 'Database mock - FONTE DI VERITÀ per tutti i dati' },
        { name: 'i18n/index.js', desc: 'Internazionalizzazione (IT, EN, ES, FR, DE)' },
      ]
    },
    components: {
      title: 'Componenti Condivisi',
      icon: 'Layers',
      color: 'blue',
      files: [
        { name: 'AdminLayout.jsx', desc: 'Layout pannello Admin con sidebar' },
        { name: 'DesktopHeader.jsx', desc: 'Header desktop con menu partner' },
        { name: 'DesktopSidebar.jsx', desc: 'Sidebar navigazione desktop' },
        { name: 'BottomNav.jsx', desc: 'Navigazione mobile bottom tab' },
        { name: 'AuthModal.jsx', desc: 'Modal login/registrazione' },
        { name: 'OfficeCard.jsx', desc: 'Card attività riutilizzabile' },
        { name: 'ItemCards.jsx', desc: 'Card per servizi, prodotti, corsi' },
        { name: 'HomeSections.jsx', desc: 'Sezioni slider homepage' },
        { name: 'CategoryIcons.jsx', desc: 'Mappatura icone categorie' },
        { name: 'AIAssistant.jsx', desc: 'Assistente AI (placeholder)' },
      ]
    }
  };

  const appPages = {
    consumer: {
      title: 'Area Consumatore',
      color: 'emerald',
      routes: [
        { route: '/', page: 'HomePage.jsx', desc: 'Homepage con categorie, offices, prodotti, corsi' },
        { route: '/search', page: 'SearchPage.jsx', desc: 'Ricerca con filtri per offices, servizi, prodotti, corsi' },
        { route: '/office/:id', page: 'OfficePage.jsx', desc: 'Dettaglio attività con servizi, prodotti, info' },
        { route: '/service/:id', page: 'DetailPages.jsx > ServiceDetailPage', desc: 'Dettaglio servizio con prenotazione' },
        { route: '/product/:id', page: 'DetailPages.jsx > ProductDetailPage', desc: 'Dettaglio prodotto con acquisto' },
        { route: '/course/:id', page: 'DetailPages.jsx > CourseDetailPage', desc: 'Dettaglio corso con iscrizione' },
        { route: '/location/:country/:region/:city', page: 'LocationPage.jsx', desc: 'Esplorazione per località' },
        { route: '/community/:id', page: 'CommunityPage.jsx', desc: 'Community locale (placeholder)' },
        { route: '/cart', page: 'CartPage.jsx', desc: 'Carrello acquisti' },
        { route: '/checkout', page: 'CheckoutPage.jsx', desc: 'Processo di checkout' },
        { route: '/order-success', page: 'OrderSuccessPage.jsx', desc: 'Conferma ordine completato' },
        { route: '/favorites', page: 'FavoritesPage.jsx', desc: 'Lista preferiti per tipo' },
        { route: '/notifications', page: 'NotificationsPage.jsx', desc: 'Centro notifiche' },
        { route: '/profile', page: 'ProfilePage.jsx', desc: 'Profilo utente, settings, switch mode' },
        { route: '/star', page: 'StarProgramPage.jsx', desc: 'Programma referral Star' },
        { route: '/elit', page: 'ElitProgramPage.jsx', desc: 'Programma fedeltà Elit' },
        { route: '/calendar', page: 'CalendarPage.jsx', desc: 'Calendario prenotazioni utente' },
        { route: '/orders', page: 'OrdersPage.jsx', desc: 'Storico ordini utente' },
      ]
    },
    partner: {
      title: 'Area Partner',
      color: 'blue',
      routes: [
        { route: '/dashboard', page: 'PartnerDashboard.jsx', desc: 'Dashboard con stats, prenotazioni, ordini recenti' },
        { route: '/calendar', page: 'CalendarPage.jsx', desc: 'Calendario gestione prenotazioni' },
        { route: '/orders', page: 'OrdersPage.jsx', desc: 'Gestione ordini e-commerce' },
        { route: '/office-catalog', page: 'OfficeCatalogPage.jsx', desc: 'Gestione catalogo servizi/prodotti/corsi' },
        { route: '/office-operators', page: 'OperatorsPage.jsx', desc: 'Gestione operatori/risorse' },
        { route: '/office-profile', page: 'OfficeProfilePage.jsx', desc: 'Modifica profilo attività' },
        { route: '/office-hours', page: 'OfficeHoursPage.jsx', desc: 'Gestione orari di apertura' },
        { route: '/daily-pricing', page: 'DailyPricingPage.jsx', desc: 'Prezzi giornalieri per noleggi' },
        { route: '/shipping-config', page: 'ShippingConfigPage.jsx', desc: 'Configurazione zone spedizione' },
        { route: '/company-settings', page: 'CompanySettingsPage.jsx', desc: 'Impostazioni azienda' },
        { route: '/company-offices', page: 'CompanyOfficesPage.jsx', desc: 'Gestione sedi multiple' },
        { route: '/payments', page: 'PaymentMethodsPage.jsx', desc: 'Metodi di pagamento' },
      ]
    },
    admin: {
      title: 'Pannello Admin',
      color: 'purple',
      routes: [
        { route: '/admin', page: 'AdminPageNew.jsx', desc: 'Dashboard admin con tutte le sezioni' },
        { route: '├── overview', page: 'OverviewSection', desc: 'Statistiche e quick actions' },
        { route: '├── schema', page: 'SchemaSection', desc: 'Schema entità database' },
        { route: '├── explorer', page: 'ExplorerSection', desc: 'Esplorazione dati JSON' },
        { route: '├── relations', page: 'RelationsSection', desc: 'Diagramma relazioni' },
        { route: '├── sitemap', page: 'SiteMapSection', desc: 'Questa pagina - Site Map' },
        { route: '├── companies', page: 'CompaniesSection', desc: 'Lista aziende' },
        { route: '├── offices', page: 'OfficesSection', desc: 'Lista sedi' },
        { route: '├── users', page: 'UsersSection', desc: 'Lista utenti' },
        { route: '├── resources', page: 'ResourcesSection', desc: 'Tutte le risorse per tipo' },
        { route: '├── categories', page: 'CategoriesSection', desc: 'Categorie attività' },
        { route: '├── services', page: 'ServicesSection', desc: 'Catalogo servizi' },
        { route: '├── products', page: 'ProductsSection', desc: 'Catalogo prodotti' },
        { route: '├── courses', page: 'CoursesSection', desc: 'Catalogo corsi' },
        { route: '├── bookings', page: 'BookingsSection', desc: 'Tutte le prenotazioni' },
        { route: '├── orders', page: 'OrdersSection', desc: 'Tutti gli ordini' },
        { route: '├── modules-config', page: 'ModulesConfigSection', desc: 'Configurazione moduli' },
        { route: '└── modules-assign', page: 'ModulesAssignSection', desc: 'Assegnazione moduli a office' },
      ]
    }
  };

  const dataFlow = [
    { from: 'database.js', to: 'AppContext.jsx', desc: 'Import dati e funzioni getter' },
    { from: 'AppContext.jsx', to: 'Tutte le pagine', desc: 'useApp() hook per stato globale' },
    { from: 'SkipresApp.jsx', to: 'Pagine', desc: 'Routing via navigate() e currentPage' },
    { from: 'Pagine Consumer', to: 'database.js', desc: 'Lettura dati (OFFICES, SERVICES, etc.)' },
    { from: 'Pagine Partner', to: 'database.js', desc: 'Lettura/Scrittura dati (mock)' },
    { from: 'AdminPageNew', to: 'database.js', desc: 'Visualizzazione completa dati' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Site Map</h2>
        <p className="text-gray-500">Struttura completa dell'applicazione: file, pagine e flussi</p>
      </div>

      {/* Struttura File */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {Object.entries(fileStructure).map(([key, section]) => (
          <div key={key} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h3 className={`font-semibold text-${section.color}-600 mb-3 flex items-center gap-2`}>
              <Server className="w-5 h-5" />
              {section.title}
            </h3>
            <div className="space-y-2">
              {section.files.map((file, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <code className="bg-gray-100 px-2 py-0.5 rounded text-xs font-mono text-gray-700 whitespace-nowrap">{file.name}</code>
                  <span className="text-gray-500 text-xs">{file.desc}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Pagine per Area */}
      <div className="space-y-4">
        {Object.entries(appPages).map(([key, area]) => (
          <div key={key} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <button
              onClick={() => toggleSection(key)}
              className={`w-full px-5 py-4 flex items-center justify-between bg-${area.color}-50 hover:bg-${area.color}-100 transition-colors`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full bg-${area.color}-500`}></div>
                <h3 className={`font-semibold text-${area.color}-700`}>{area.title}</h3>
                <span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded-full">{area.routes.length} pagine</span>
              </div>
              {expandedSections.includes(key) ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
            </button>
            {expandedSections.includes(key) && (
              <div className="p-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 uppercase">
                      <th className="pb-2 w-1/4">Route</th>
                      <th className="pb-2 w-1/4">File</th>
                      <th className="pb-2">Descrizione</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {area.routes.map((route, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="py-2 font-mono text-xs text-emerald-600">{route.route}</td>
                        <td className="py-2 text-xs text-blue-600">{route.page}</td>
                        <td className="py-2 text-xs text-gray-600">{route.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Flusso Dati */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <GitBranch className="w-5 h-5 text-amber-500" />
          Flusso Dati
        </h3>
        <div className="space-y-2">
          {dataFlow.map((flow, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <code className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs">{flow.from}</code>
              <span className="text-gray-400">→</span>
              <code className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded text-xs">{flow.to}</code>
              <span className="text-gray-500 text-xs ml-2">{flow.desc}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Moduli e loro pagine */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-5 border border-indigo-100">
        <h3 className="font-semibold text-indigo-700 mb-4">Moduli → Funzionalità</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="bg-white/70 rounded-lg p-3">
            <p className="font-medium text-sm text-gray-900">appointments</p>
            <p className="text-xs text-gray-500 mt-1">Servizi, Prenotazioni slot, CalendarPage, OperatorsPage</p>
          </div>
          <div className="bg-white/70 rounded-lg p-3">
            <p className="font-medium text-sm text-gray-900">rentals</p>
            <p className="text-xs text-gray-500 mt-1">Noleggi giornalieri, DailyPricingPage, Risorse (vehicle, boat, apartment)</p>
          </div>
          <div className="bg-white/70 rounded-lg p-3">
            <p className="font-medium text-sm text-gray-900">courses</p>
            <p className="text-xs text-gray-500 mt-1">Corsi settimanali, CourseDetailPage, Istruttori, Sale</p>
          </div>
          <div className="bg-white/70 rounded-lg p-3">
            <p className="font-medium text-sm text-gray-900">ecommerce</p>
            <p className="text-xs text-gray-500 mt-1">Prodotti, CartPage, CheckoutPage, ShippingConfigPage, OrdersPage</p>
          </div>
          <div className="bg-white/70 rounded-lg p-3">
            <p className="font-medium text-sm text-gray-900">tables</p>
            <p className="text-xs text-gray-500 mt-1">Prenotazione tavoli, Gestione coperti, Risorse (table, area)</p>
          </div>
          <div className="bg-white/70 rounded-lg p-3">
            <p className="font-medium text-sm text-gray-900">events</p>
            <p className="text-xs text-gray-500 mt-1">Eventi singoli (placeholder - non implementato)</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AdminPageNew = ({ onBack, onNavigate: parentNavigate }) => {
  const [currentSection, setCurrentSection] = useState('overview');

  const handleLogout = () => {
    if (onBack) onBack();
  };

  const renderSection = () => {
    switch (currentSection) {
      case 'overview':
        return <OverviewSection onNavigate={setCurrentSection} />;
      case 'schema':
        return <SchemaSection />;
      case 'explorer':
        return <ExplorerSection />;
      case 'relations':
        return <RelationsSection />;
      case 'sitemap':
        return <SiteMapSection />;
      case 'companies':
        return <CompaniesSection />;
      case 'offices':
        return <OfficesSection />;
      case 'users':
        return <UsersSection />;
      case 'resources':
        return <ResourcesSection />;
      case 'categories':
        return <CategoriesSection />;
      case 'services':
        return <ServicesSection />;
      case 'rentals':
        return <RentalsSection />;
      case 'products':
        return <ProductsSection />;
      case 'events':
        return <EventsSection />;
      case 'courses':
        return <CoursesSection />;
      case 'bookings':
        return <BookingsSection />;
      case 'orders':
        return <OrdersSection />;
      case 'modules-config':
        return <ModulesConfigSection />;
      case 'modules-assign':
        return <ModulesAssignSection />;
      case 'communities':
        return <CommunitySection />;
      case 'locations':
        return <LocationsSection />;
      case 'star-program':
        return <StarProgramSection />;
      case 'elit-program':
        return <ElitProgramSection />;
      case 'statuses':
        return <StatusesSection />;
      case 'platform':
        return <PlatformSection />;
      default:
        return <OverviewSection onNavigate={setCurrentSection} />;
    }
  };

  return (
    <AdminLayout 
      currentSection={currentSection} 
      onSectionChange={setCurrentSection}
      onLogout={handleLogout}
    >
      {renderSection()}
    </AdminLayout>
  );
};

export default AdminPageNew;
